import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  ArrowRight, 
  CheckCircle, 
  FileText,
  Sparkles,
  Shield
} from 'lucide-react';

const BENEFITS = [
  "AI-powered review generation",
  "Automatic skill detection",
  "Professional formatting",
  "Easy to copy and customize",
  "Build your Living CV over time"
];

const FinalCTA = ({ series }) => {
  const navigate = useNavigate();
  const accentColor = series?.accentColor || '#dc2626';

  const handleGetStarted = () => {
    // For unauthenticated users, go to signup
    // For authenticated, go to dashboard review page
    const token = localStorage.getItem('auth_token');
    if (token) {
      navigate('/dashboard/review');
    } else {
      navigate('/login');
    }
  };

  return (
    <section className="py-20 bg-slate-900" data-testid="review-final-cta">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 lg:p-12 border border-slate-700 relative overflow-hidden">
            {/* Background decoration */}
            <div 
              className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20"
              style={{ backgroundColor: accentColor }}
            />
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm text-white mb-6">
                  <Sparkles className="w-4 h-4" />
                  <span>Ready to write your best review?</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Stop dreading performance reviews
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  Join thousands of professionals who use Companion to turn their work into compelling evidence. Your next review could take minutes, not hours.
                </p>
              </div>

              {/* Benefits */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                {BENEFITS.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg border border-white/10"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-slate-200">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={handleGetStarted}
                  size="lg"
                  className="px-8 py-4 text-white text-lg font-medium"
                  style={{ backgroundColor: accentColor }}
                  data-testid="review-cta-start"
                >
                  Start my review
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => document.getElementById('review-demo')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 text-white border-white/30 hover:bg-white/10"
                  data-testid="review-cta-demo"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  See example output
                </Button>
              </div>

              {/* Trust note */}
              <div className="flex items-center justify-center gap-2 mt-8 text-sm text-slate-400">
                <Shield className="w-4 h-4" />
                <span>Your data is private and secure. We never share your information.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
